import {
  AlertTriangle,
  Clock3,
  Library,
  Loader2,
  Package,
  Plus,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { AppShell, Breadcrumbs } from '@/components/landing/AppShell';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import {
  packDisplayTitle,
  packEstimatedMinutes,
} from '@/lib/lesson-pack-display';
import type {
  LessonPackListItem,
  LessonPackStatus,
} from '@/lib/schema.types';
import { cn } from '@/lib/utils';

/** Refetch the list every 5s so generating packs flip to `done` on their own. */
const REFRESH_MS = 5000;

export default function LessonPacksPage() {
  const navigate = useNavigate();
  const { data, error, isLoading, mutate } = useSWR<LessonPackListItem[]>(
    '/lesson-packs',
    () => api.lessonPacks.list(),
    { refreshInterval: REFRESH_MS, keepPreviousData: true },
  );

  // Pause the auto-refresh once nothing is in flight to be polite to the API.
  const anyInFlight = (data ?? []).some(
    (p) => p.status === 'pending' || p.status === 'generating',
  );
  useEffect(() => {
    if (!anyInFlight) return;
    const t = setInterval(() => mutate(), REFRESH_MS);
    return () => clearInterval(t);
  }, [anyInFlight, mutate]);

  const packs = data ?? [];

  // Draft vs Published toggle (default Draft). A pack with no publishState is
  // treated as a draft. Filtered client-side — the whole list is already loaded.
  const [tab, setTab] = useState<'draft' | 'published'>('draft');
  const draftCount = packs.filter((p) => p.publishState !== 'published').length;
  const publishedCount = packs.length - draftCount;
  const visible = packs.filter((p) =>
    tab === 'published'
      ? p.publishState === 'published'
      : p.publishState !== 'published',
  );

  return (
    <AppShell activeNav="lesson-packs">
      <div className="mx-auto flex w-full max-w-[1100px] flex-col px-8 pt-6 pb-16">
        <Breadcrumbs
          items={[{ label: 'Lesson Packs' }]}
        />

        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[26px] font-semibold tracking-tight text-fg">
              Lesson Packs
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              AI-generated, editable lesson packs for the offline classroom.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/curriculum">
              <Button variant="secondary" size="md" className="px-4">
                <Library size={15} /> Curriculum
              </Button>
            </Link>
            <Link to="/lesson-packs/new">
              <Button variant="primary" size="md" className="px-5">
                <Plus size={15} /> New Lesson Pack
              </Button>
            </Link>
          </div>
        </div>

        {isLoading && (
          <div className="mt-12 flex justify-center text-fg-muted">
            <Loader2 size={18} className="animate-spin" />
          </div>
        )}

        {error && (
          <div className="mt-6 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">
            <AlertTriangle size={14} />
            <span>
              Could not load lesson packs.{' '}
              {error instanceof Error ? error.message : ''}
            </span>
          </div>
        )}

        {!isLoading && !error && packs.length === 0 && <EmptyState />}

        {packs.length > 0 && (
          <>
            <PackTabs
              tab={tab}
              onChange={setTab}
              draftCount={draftCount}
              publishedCount={publishedCount}
            />

            {visible.length === 0 ? (
              <p className="mt-10 text-center text-[13px] text-fg-muted">
                No {tab} lesson packs yet.
              </p>
            ) : (
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((pack) => (
                  <PackCard
                    key={pack._id}
                    pack={pack}
                    onOpen={() => navigate(`/lesson-packs/${pack._id}`)}
                    onDelete={async () => {
                      if (!confirm(`Delete "${pack.title}"?`)) return;
                      await api.lessonPacks.remove(pack._id);
                      mutate();
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function PackTabs({
  tab,
  onChange,
  draftCount,
  publishedCount,
}: {
  tab: 'draft' | 'published';
  onChange: (t: 'draft' | 'published') => void;
  draftCount: number;
  publishedCount: number;
}) {
  const tabs: { id: 'draft' | 'published'; label: string; count: number }[] = [
    { id: 'draft', label: 'Draft', count: draftCount },
    { id: 'published', label: 'Published', count: publishedCount },
  ];
  return (
    <div className="mt-6 inline-flex self-start rounded-lg border border-border bg-bg-subtle p-0.5">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-[13px] font-medium transition',
            tab === t.id
              ? 'bg-bg-panel text-fg shadow-sm'
              : 'text-fg-muted hover:text-fg',
          )}
        >
          {t.label}
          <span
            className={cn(
              'rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
              tab === t.id
                ? 'bg-accent-soft text-accent'
                : 'bg-border-subtle text-fg-muted',
            )}
          >
            {t.count}
          </span>
        </button>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mt-10 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-bg-panel/50 py-16 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-xl bg-accent-soft text-accent">
        <Package size={24} />
      </span>
      <h3 className="mt-4 text-base font-semibold text-fg">
        No lesson packs yet
      </h3>
      <p className="mt-1.5 max-w-sm text-[13px] text-fg-muted">
        Generate your first lesson pack from a chapter or topic in the curriculum, or start with a blank slate and fill in the beats yourself.
      </p>
      <Link to="/lesson-packs/new" className="mt-5">
        <Button variant="primary" size="md">
          <Plus size={14} /> Generate first lesson pack
        </Button>
      </Link>
    </div>
  );
}

function PackCard({
  pack,
  onOpen,
  onDelete,
}: {
  pack: LessonPackListItem;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const inFlight = pack.status === 'pending' || pack.status === 'generating';

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onOpen}
        className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-border-subtle bg-bg-panel text-left transition-shadow hover:shadow-slide"
      >
        <div className="relative flex aspect-[16/10] items-center justify-center overflow-hidden bg-bg-subtle">
          <PackCover title={pack.title} thumbnailUrl={pack.thumbnailUrl} />
          <div className="absolute left-3 top-3 flex gap-1.5">
            <StatusBadge status={pack.status} />
            {pack.publishState && (
              <PublishBadge state={pack.publishState} />
            )}
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-2 px-4 py-3">
          <p className="line-clamp-2 text-[13.5px] font-semibold leading-tight text-fg">
            {packDisplayTitle(pack)}
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-fg-muted">
            <span>{pack.board}</span>
            <span>·</span>
            <span>{pack.grade}</span>
            <span>·</span>
            <span className="capitalize">{pack.subject}</span>
          </div>
          {pack.chapterReference && (
            <p className="line-clamp-1 text-[11.5px] text-fg-subtle">
              {pack.chapterReference}
            </p>
          )}
          <div className="mt-auto flex items-center justify-between text-[11.5px] text-fg-muted">
            <span className="inline-flex items-center gap-1">
              <Clock3 size={11} />
              {pack.createdAt ? relativeTime(pack.createdAt) : 'just now'}
            </span>
            <span>{packEstimatedMinutes(pack)} min</span>
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete();
        }}
        className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-md bg-white/90 text-fg-muted opacity-0 shadow-pill transition-opacity hover:bg-white hover:text-red-500 group-hover:opacity-100"
        title="Delete"
        disabled={inFlight}
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}

function StatusBadge({ status }: { status: LessonPackStatus }) {
  if (status === 'done') {
    return (
      <span className="rounded-md bg-fg/85 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white">
        Ready
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-red-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white">
        <AlertTriangle size={9} /> Error
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white">
      <Loader2 size={9} className="animate-spin" />
      {status === 'pending' ? 'Queued' : 'Generating'}
    </span>
  );
}

function PublishBadge({ state }: { state: 'draft' | 'published' }) {
  if (state === 'published') {
    return (
      <span className="rounded-md bg-emerald-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white">
        Published
      </span>
    );
  }
  return (
    <span className="rounded-md bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-muted">
      Draft
    </span>
  );
}

function PackCover({
  title,
  thumbnailUrl,
}: {
  title: string;
  thumbnailUrl?: string | null;
}) {
  const hue = ((title?.length ?? 7) * 53) % 360;
  return (
    <div className="relative h-full w-full">
      {/* Gradient placeholder sits underneath; shows through when there's no
       *  thumbnail or the image URL fails to load. */}
      <div
        className={cn(
          'flex h-full w-full items-center justify-center text-fg-inverted',
        )}
        style={{
          background: `linear-gradient(135deg, hsl(${hue} 60% 55%), hsl(${(hue + 90) % 360} 65% 45%))`,
        }}
      >
        <Package size={32} className="opacity-95" />
      </div>
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
    </div>
  );
}

function relativeTime(iso: string) {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} day${d === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
